import React, { ReactElement } from 'react';
import clsx from 'clsx';
import Image from 'next/image';

import { CartProps } from '~/types/cart';
import { currencyWithCents } from '~/lib/format';

import {
  ApplyCartButton,
  RemoveCartButton,
} from '~/components/account/Discounts';
import ErrorCircle from '~/public/font-awesome/exclamation-circle-red.png';
import credits from '~/public/credits.svg';

interface CreditsExpandedProps {
  cart?: CartProps;
  error: string;
  isCreditsPending: boolean;
  onClickToggle: () => void;
}

export default React.memo(function CreditsExpandedProps({
  cart,
  error,
  isCreditsPending,
  onClickToggle,
}: Readonly<CreditsExpandedProps>): ReactElement {
  const isCreditsApplied = !!cart?.credit;

  const creditAmountText = (): string => {
    if (!cart) return '';

    if (cart.use_credit) {
      return `${currencyWithCents(cart.credit)} applied!`;
    }

    return `${currencyWithCents(cart.available_credit)} available!`;
  };

  const toggleClass = clsx('ml-auto', {
    'pointer-events-none select-none': isCreditsPending,
  });

  return (
    <div data-testid="credit-expanded-component">
      <div
        className={clsx(
          'flex flex-col justify-between rounded border p-4 md:flex-row',
          error ? 'border-2 border-brick' : 'border-sundog',
        )}
      >
        <div className="flex w-full items-center">
          <Image
            src={credits}
            alt="credits-icon"
            className="mr-4 h-6 w-6"
            height={24}
            width={24}
          />
          <div className="flex flex-col">
            <p>Apply AppSumo credits</p>
            <p
              className="font-bold text-money-moss"
              data-testid="credit-amount-text"
            >
              {creditAmountText()}
            </p>
          </div>

          <div className={toggleClass}>
            <div className="flex flex-col justify-center md:gap-2">
              <ApplyCartButton
                isApplied={isCreditsApplied}
                isLoading={isCreditsPending}
                onClick={onClickToggle}
              />
              <RemoveCartButton
                isApplied={isCreditsApplied}
                isLoading={isCreditsPending}
                onClick={onClickToggle}
              />
            </div>
          </div>
        </div>
      </div>
      {!!error && (
        <p className="mt-2 flex items-center text-sm text-brick">
          <Image
            height={16}
            width={16}
            src={ErrorCircle}
            alt="error-circle"
            className="mr-2 inline-block"
          />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
});
